import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronRight, Plus, X } from 'lucide-react'
import { getSupabase } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen, TopBar } from '../components/mobile-screen'
import { CoverPicker, type CoverPick } from '../components/modals/cover-picker'
import { PAID_MIN_HINT, createPack, fileInfoOptionFromMine, type FileInfoOption, type FileInfoMineRow, type CreatePackInput } from '../_lib/pack-api'
import { PackAddFilesPage } from '../components/pack-add-files-page'
import { PackFilesView } from '../components/pack-files-view'
import { FileIcon } from '../components/file-icon'
import { PackPriceRow } from '../components/pack-price-row'
import { resourceUrl } from '../_lib/resource-node-pref'

/**
 * 创建资源包 — 2026-06-02(D8)仅付费(access_mode='paid')。
 *
 * 删 free 全链路:免费分享 tab / 提取码 / 有效期选项均移除(spec §5)。价格必填且
 * `>= pack.paid_min_price_coin`(默认 10;后端校验,不足返 PRICE_BELOW_MIN)。
 * 此处 PAID_MIN_HINT 仅用于 UI 文案提示;权威值在后端 config。
 *
 * 2026-06-07:添加文件改 UGuBc 全屏选择页(文件夹下钻+勾选,前端递归展开;旧底部弹层删除)。
 *
 * 2026-06-08 pen 改版(FB0fg):删 validityHint(永久有效绿框)/ 允许转存·允许下载 toggle
 * (创建时固定 true,创建后可在分享设置 ondv5 改)/ qrBtn simuZ(显示二维码;底部仅创建按钮);
 * coverCard(RF3QP)改卡片式并移入「封面与标签」小节(价格之后)。
 *
 * 2026-06-07:多选分享入口:?file_ids= 预选(BatchActionBar 批量分享)
 *   - ?file_ids=a,b,c  → 批量分享入口(BatchActionBar;全是 type='file' id,不含文件夹)
 *   - ?file_id=x       → 单文件兼容(file-actions onShare 旧参数,file_ids 优先)
 *   pack item 只能是 type='file':查询加 .neq('type','folder') 防御。
 *
 * 2026-06-11 pen 对齐:价格区改 hfPqb 单行胶囊(PackPriceRow 共用);文件行 grip/FileIcon/X。
 *
 * 2026-06-13 pen 改版(FB0fg):新增名称(必填)/介绍(≤100 字)输入区;封面改全网盘浏览
 * (统一 CoverPicker,与已选文件解耦);文件列表改卡片式
 * 「前 2 项 + 查看全部文件」(BQc4x → 页内全屏 PackFilesView local 模式)。
 */

export function PackCreatePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [selected, setSelected] = useState<FileInfoOption[]>([])
  // 预选 seed:只执行一次(seeded guard);file_ids 优先,兼容旧 file_id 单文件参数
  const seeded = useRef(false)
  useEffect(() => {
    if (seeded.current) return
    const rawIds = searchParams.get('file_ids') ?? searchParams.get('file_id')
    if (!rawIds) return
    seeded.current = true
    let cancelled = false
    const ids = rawIds
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    if (ids.length === 0) return
    void (async () => {
      const sb = getSupabase()
      const { data } = await sb
        .from('v_file_info_mine')
        .select('id, name, type, base_file_id, thumb_url_list, size_original, mime_type, created_at')
        .in('id', ids)
        .neq('type', 'folder') // pack item 只能是 file;防御含文件夹的入口(如手动构造 URL)
      if (cancelled || !data) return
      const files = (data as unknown as FileInfoMineRow[]).map(fileInfoOptionFromMine)
      if (files.length > 0) setSelected(files)
    })()
    return () => {
      cancelled = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [priceCoin, setPriceCoin] = useState<number>(PAID_MIN_HINT)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showAddFiles, setShowAddFiles] = useState(false)
  const [showCover, setShowCover] = useState(false)
  const [showAllFiles, setShowAllFiles] = useState(false)
  // 封面来自全网盘浏览(统一 CoverPicker),与已选文件解耦
  const [cover, setCover] = useState<CoverPick | null>(null)

  const toggle = (f: FileInfoOption) => {
    setSelected((prev) =>
      prev.find((x) => x.id === f.id) ? prev.filter((x) => x.id !== f.id) : [...prev, f],
    )
  }

  const onSubmit = async () => {
    setBusy(true)
    setError(null)
    try {
      if (!name.trim()) throw new Error('请输入资源包名称')
      if (selected.length === 0) throw new Error('请至少选 1 个文件')
      if (priceCoin < PAID_MIN_HINT) throw new Error(`价格不得低于 ${PAID_MIN_HINT} 金币`)
      const body: CreatePackInput = {
        access_mode: 'paid',
        name: name.trim(),
        description: description.trim() || undefined,
        // 封面随 create 一并提交(后端 validateCover 校验);
        // 旧 supabase 直写 resource_pack.cover_file_info_id 通道已 REVOKE(20260612000200)
        cover_file_info_id: cover?.id ?? undefined,
        price_coin: priceCoin,
        password: null,
        expires_at: null,
        // 2026-06-08 pen 改版:创建页删 toggle,固定放开;创建后可在分享设置(ondv5)调整
        allow_save_to_pan: true,
        allow_download: true,
        items: selected.map((f, i) => ({ file_info_id: f.id, position: i })),
      }
      await createPack(body)
      navigate('/files?view=shares&tab=mine')
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppLayout>
      <MobileScreen>
        <TopBar title="创建资源包" />

        {/* FB0fg 付费订阅(资源包仅付费;免费分享 tab 已删) */}
        <main data-testid="FB0fg" className="flex flex-1 flex-col gap-6 px-6 pt-4 pb-32">
          {/* NameSection 资源包名称(必填;后端 validatePackName 兜底) */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold text-[#1A1A1A]">资源包名称</h2>
            <input
              data-testid="FB0fg-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="请输入资源包名称"
              className="h-[52px] rounded-xl bg-[#F6F7F8] px-4 text-[16px] outline-none placeholder:text-[#9CA3AF]"
            />
          </section>

          {/* IntroSection 资源包介绍(≤100 字;后端兜底) */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold text-[#1A1A1A]">资源包介绍</h2>
            <div className="flex flex-col gap-2 rounded-xl bg-[#F6F7F8] px-4 py-3.5">
              <textarea
                data-testid="FB0fg-intro-input"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                placeholder="介绍一下这个资源包的内容,帮助用户更好地了解..."
                rows={3}
                className="min-h-[68px] resize-none bg-transparent text-[15px] leading-[1.4] outline-none placeholder:text-[#9CA3AF]"
              />
              <span data-testid="FB0fg-intro-counter" className="text-right text-[13px] text-[#C4C8CF]">{description.length}/100</span>
            </div>
          </section>

          {/* MetaSection ZFYdj 封面与标签(FB0fg:移到价格前) */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold text-[#1A1A1A]">封面与标签</h2>
            <button
              type="button"
              data-testid="RF3QP"
              onClick={() => setShowCover(true)}
              className="flex w-full items-center gap-3 rounded-xl bg-[#F6F7F8] p-3 text-left"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[#E6CCFF] text-[#5B21B6]">
                {cover?.thumbUrl ? (
                  <img src={resourceUrl(cover.thumbUrl)} alt="封面" className="h-full w-full object-cover" />
                ) : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-medium text-[#1A1A1A]">封面</span>
                <span className="block truncate text-[11px] text-[#9CA3AF]">
                  {cover ? cover.name : '付费包建议设置封面 · 影响订阅转化'}
                </span>
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </section>

          <PackPriceRow value={priceCoin} min={PAID_MIN_HINT} onChange={setPriceCoin} />

          {/* 文件列表区(FB0fg FileSection):标题 + 添加文件 + 文件卡(查看全部在顶 + 前 5 项) */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[15px] font-semibold text-[#1A1A1A]">文件列表</h2>
            <button
              type="button"
              onClick={() => setShowAddFiles(true)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] bg-white text-[16px] font-medium text-[#19caad]"
            >
              <Plus size={18} aria-hidden="true" />
              添加文件
            </button>
            {selected.length > 0 && (
              <div className="flex flex-col gap-2 rounded-xl bg-[#F6F7F8] p-3">
                <button
                  type="button"
                  data-testid="BQc4x"
                  onClick={() => setShowAllFiles(true)}
                  className="flex items-center justify-between px-1 pb-2 text-[13px] font-medium text-[#19caad]"
                >
                  查看全部文件
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
                {selected.slice(0, 5).map((f) => (
                  <div key={f.id} className="flex items-center gap-3">
                    <FileIcon type={f.type} mimeType={f.mime_type} name={f.name} size={36} iconSize={16} />
                    <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-[#1A1A1A]">{f.name}</span>
                    <button type="button" onClick={() => toggle(f)} aria-label="移除" className="shrink-0 text-[#9CA3AF]">
                      <X size={20} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && <p className="text-[13px] text-[#FF6B6B]">{error}</p>}
        </main>

        <div
          className="fixed inset-x-0 bottom-0 z-20 mx-auto flex max-w-[402px] flex-col gap-2 bg-white px-6 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || !name.trim() || selected.length === 0 || priceCoin < PAID_MIN_HINT}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16.5 9.4L7.55 4.24M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" /></svg>
            {busy ? '创建中…' : '创建资源包'}
          </button>
        </div>

        <CoverPicker
          open={showCover}
          onClose={() => setShowCover(false)}
          currentCoverId={cover?.id ?? null}
          onSelect={(pick) => setCover(pick)}
          onClear={() => setCover(null)}
        />

        {showAddFiles && (
          <PackAddFilesPage
            initialSelected={selected}
            onCancel={() => setShowAddFiles(false)}
            onConfirm={(files) => {
              setSelected(files)
              setShowAddFiles(false)
            }}
          />
        )}

        {showAllFiles && (
          <PackFilesView
            onBack={() => setShowAllFiles(false)}
            rows={selected.map((f) => ({ key: f.id, name: f.name, type: f.type, size: f.size, mimeType: f.mime_type }))}
          />
        )}
      </MobileScreen>
    </AppLayout>
  )
}
